'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Lock, User, AlertTriangle, Shield, Zap } from 'lucide-react';
import dynamic from 'next/dynamic';
import { createClient } from '@/lib/supabase/client';

const AnimatedBackground = dynamic(() => import('./AnimatedBackground'), { ssr: false });
const ForexTicker = dynamic(() => import('./ForexTicker'), { ssr: false });

interface LoginForm {
  username: string;
  password: string;
}

export default function LoginClient() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>();

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    setErrorMsg('');

    try {
      const supabase = createClient();

      // Resolve username → email via RPC
      const { data: email, error: rpcError } = await supabase
        .rpc('get_email_by_username', { p_username: data.username.trim() });

      if (rpcError || !email) {
        setErrorMsg('Username not found. Please check your credentials.');
        setIsLoading(false);
        return;
      }

      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email,
        password: data.password,
      });

      if (error || !authData.user) {
        const msg = error?.message || '';
        if (msg.toLowerCase().includes('invalid login') || msg.toLowerCase().includes('invalid credentials')) {
          setErrorMsg('Incorrect password. Please try again.');
        } else if (msg.toLowerCase().includes('email not confirmed')) {
          setErrorMsg('Account not confirmed. Contact admin.');
        } else {
          setErrorMsg(msg || 'Invalid credentials. Check your username and password.');
        }
      } else {
        // Role is stored in user_metadata — no extra DB round-trip needed
        const role = authData.user.user_metadata?.role || 'trader';
        if (role === 'admin') {
          router.push('/admin-panel');
        } else {
          router.push('/trading-dashboard');
        }
      }
    } catch {
      setErrorMsg('An unexpected error occurred. Please try again.');
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden" style={{ background: 'var(--background)' }}>
      <AnimatedBackground />
      <ForexTicker />

      {/* Radial glow behind card */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(0,200,240,0.06) 0%, transparent 70%)',
        }}
      />

      <div className="relative z-10 w-full max-w-md mx-4">
        {/* Header */}
        <div className="text-center mb-8 animate-fade-in-up">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 border border-primary/30 flex items-center justify-center neon-glow-cyan">
              <Zap size={24} className="text-primary" />
            </div>
            <div className="text-left">
              <h1 className="text-2xl font-bold neon-text-cyan tracking-wider">SPECTRA SMC</h1>
              <p className="text-xs font-mono text-muted-foreground">EA ROBOT TERMINAL v2.1</p>
            </div>
          </div>
          <div className="flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-warning/10 border border-warning/30 inline-flex mx-auto w-fit">
            <Shield size={13} className="text-warning" />
            <span className="text-xs font-mono text-warning">INVITATION ONLY — NO PUBLIC ACCESS</span>
          </div>
        </div>

        {/* Card */}
        <div className="glass-card-elevated rounded-2xl p-8 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          {/* Error message */}
          {errorMsg && (
            <div className="flex items-start gap-3 p-3 rounded-xl mb-6 border bg-danger/10 border-danger/30">
              <AlertTriangle size={16} className="text-danger mt-0.5 flex-shrink-0" />
              <p className="text-sm font-medium text-danger">{errorMsg}</p>
            </div>
          )}


          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Username */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2">
                Username
              </label>
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  autoComplete="username"
                  {...register('username', { required: 'Username is required' })}
                  className={`
                    w-full pl-10 pr-4 py-3 rounded-xl input-cyber text-sm
                    ${errors.username ? 'input-cyber-error' : ''}
                  `}
                  placeholder="Enter username"
                />
              </div>
              {errors.username && (
                <p className="text-xs text-danger mt-1.5">{errors.username.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2">
                Password
              </label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  {...register('password', { required: 'Password is required' })}
                  className={`
                    w-full pl-10 pr-12 py-3 rounded-xl input-cyber text-sm
                    ${errors.password ? 'input-cyber-error' : ''}
                  `}
                  placeholder="Enter password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-danger mt-1.5">{errors.password.message}</p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 rounded-xl btn-primary text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  <span>Authenticating...</span>
                </>
              ) : (
                <>
                  <Zap size={16} />
                  <span>Access Terminal</span>
                </>
              )}
            </button>
          </form>

          {/* Footer notice */}
          <div className="mt-6 pt-5 border-t border-border/50 text-center">
            <p className="text-xs text-muted-foreground">
              Access is granted by invitation only.{' '}
              <span className="text-primary">Contact admin for credentials.</span>
            </p>
            <p className="text-xs font-mono text-muted-foreground/50 mt-2">
              Unauthorized access attempts are logged.
            </p>
          </div>
        </div>

        {/* Bottom tag */}
        <p className="text-center text-xs font-mono text-muted-foreground/40 mt-6">
          SPECTRA SMC EA TERMINAL — SECURED PLATFORM — 2026
        </p>
      </div>
    </div>
  );
}